import { Suspense, lazy } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { FormPage } from "./pages/FormPage.js";
import { FormSkeleton } from "./components/FormSkeleton.js";

/**
 * Phase 22 / Phase 329 — Subject form portal SPA root.
 *
 * <p>Two audiences over one renderer:</p>
 * <ul>
 *   <li><b>the client form</b> — any other path, with the magic-link token read from
 *       `?t=<token>`. Server-side token validation is its whole auth surface (CONTEXT.md D-04):
 *       no provider, no gate, no account;</li>
 *   <li><b>the staff form</b> — `/staff/:jobId/:formId`, a credential-free deep link behind
 *       operator sign-in, plus the `/auth/callback` landing that completes the redirect.</li>
 * </ul>
 *
 * <h3>Why the two authenticated routes are lazy</h3>
 * <p>They are the only modules that reach MSAL, and a subject following a magic link must never
 * load an identity library: they have no account to sign into, it is dead weight on a phone, and
 * an auth module that runs on a public page is a surface with nothing to gain from existing. The
 * dynamic `import()` keeps the whole staff path — gate, MSAL, staff client — in a chunk the
 * client page never fetches, which is a build-level guarantee rather than a convention.
 * `fences.test.ts` pins that these two imports stay dynamic and that MSAL stays confined to
 * `src/auth/`.</p>
 */

const StaffFormRoute = lazy(() =>
  import("./pages/StaffFormPage.js").then((m) => ({ default: m.StaffFormRoute })),
);

const AuthCallback = lazy(() =>
  import("./auth/AuthCallback.js").then((m) => ({ default: m.AuthCallback })),
);

export function App() {
  return (
    <BrowserRouter>
      {/* The same skeleton the form itself loads behind, so fetching the staff chunk looks like
          what it is — the page loading — rather than like a second, different wait. */}
      <Suspense fallback={<div className="form-page"><FormSkeleton /></div>}>
        <Routes>
          <Route path="/staff/:jobId/:formId" element={<StaffFormRoute />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={<FormPage />} />
          <Route path="*" element={<FormPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
